import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ReactiveFormsModule, FormControl, FormGroup, Validators, FormBuilder, ValidatorFn } from '@angular/forms';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { FieldType, FieldConfig, ProductOrganizationDialogConfig } from '../../product.model';
import { Result } from '@/reservation/reservation.model';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, switchMap } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

const PHONE_REGEX = /^\+?[0-9]\d{1,14}$/;

@Component({
	selector: 'app-product-dynamic-form-dialog',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		MatDialogContent,
		MatDialogActions,
		MatAutocompleteModule,
	],
	templateUrl: './product-dynamic-form-dialog.component.html',
	styleUrl: './product-dynamic-form-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDynamicFormDialogComponent implements OnInit {
	form!: FormGroup<Record<string, FormControl>>;

	readonly data = inject<ProductOrganizationDialogConfig>(MAT_DIALOG_DATA);

	readonly FieldType = FieldType;
	readonly appearance: MatFormFieldAppearance = 'outline';

	private readonly dialogRef = inject(MatDialogRef);
	private readonly fb = inject(FormBuilder);
	private readonly httpClient = inject(HttpClient);
	private readonly cdr = inject(ChangeDetectorRef);

	ngOnInit(): void {
		const group = this.fb.group<{ [key: string]: FormControl<unknown | null> }>({});

		for (const f of this.data.fields) {
			group.addControl(
				f.key,
				this.fb.control(
					{ value: f.initialValue ?? '', disabled: !!f.disabled },
					{ validators: this.buildValidators(f) },
				),
			);
		}

		this.form = group;

		for (const f of this.data.fields) {
			if (f.type === FieldType.SELECT) {
				this.form
					.get(f.key)
					?.valueChanges.pipe(
						startWith(''),
						debounceTime(200),
						distinctUntilChanged(),
						switchMap((searchValue: string | unknown) =>
							this.fetchOptions(
								f.apiOptions.apiKey,
								f.apiOptions.searchApiKey,
								typeof searchValue !== 'string' ? '' : searchValue,
							),
						),
						map((response) => response.results),
					)
					.subscribe((val) => {
						f.options = val;
						this.cdr.detectChanges();
					});
			}
		}
	}

	getDisplayFn(field: FieldConfig): (value: unknown) => string {
		return (value: unknown): string => {
			if (!value) {
				return '';
			}

			if (typeof value === 'object' && field.apiOptions.labelApiKey in value) {
				return value[field.apiOptions.labelApiKey] as string;
			}

			const found = field.options.find((o) => o[field.apiOptions.valueApiKey] === value);
			return found?.name ?? '';
		};
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.form.valid) {
			this.dialogRef.close({ [this.data.configType]: this.form.getRawValue() });
		}
	}

	private fetchOptions(apiKey: string, searchApiKey: string, searchValue: string): Observable<Result<unknown>> {
		return this.httpClient.get<Result<unknown>>(`${environment.apiUrl}/${apiKey}/?${searchApiKey}=${searchValue}`);
	}

	private buildValidators(f: FieldConfig): ValidatorFn[] {
		const validators: ValidatorFn[] = [];

		if (f.required) {
			validators.push(Validators.required);
		}

		const typeValidators: Partial<Record<FieldType, ValidatorFn[]>> = {
			email: [Validators.email],
			phone: [Validators.pattern(PHONE_REGEX)],
		};

		validators.push(...(typeValidators[f.type] ?? []));

		if (this.isNumber(f.min)) {
			validators.push(Validators.min(f.min));
		}

		if (this.isNumber(f.max)) {
			validators.push(Validators.max(f.max));
		}

		return validators;
	}

	private isNumber(v: unknown): v is number {
		return typeof v === 'number' && Number.isFinite(v);
	}
}
